import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Search,
  FileText,
  MessageSquare,
  Users,
  CheckCircle2,
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  CreditCard,
  Briefcase,
  Car,
  DollarSign,
  Clock,
  FilePlus,
  ShoppingCart,
  Download,
  KeyRound,
  StickyNote,
  Trash2,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Logo from "@/components/Logo";
import PurchaseOrders from "@/pages/PurchaseOrders";

const APP_STATUSES = ["new", "contacted", "approved", "declined", "archived"];
const CONTACT_STATUSES = ["new", "responded", "archived"];

const STATUS_COLORS = {
  new: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  declined: "bg-red-500/15 text-red-300 border-red-500/30",
  archived: "bg-white/5 text-white/40 border-white/10",
  responded: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1, limit: 25 });
  const [exporting, setExporting] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [poPrefill, setPoPrefill] = useState(null);

  const PAGE_LIMIT = 25;

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      /* ignore */
    }
  }, []);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_LIMIT };
      if (statusFilter !== "all") params.status = statusFilter;
      if (cityFilter !== "all") params.city = cityFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/applications", { params });
      setApps(data.items || []);
      setPageInfo({ total: data.total || 0, pages: data.pages || 1, limit: data.limit || PAGE_LIMIT });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to load applications."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, cityFilter, search, page]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_LIMIT };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/contacts", { params });
      setContacts(data.items || []);
      setPageInfo({ total: data.total || 0, pages: data.pages || 1, limit: data.limit || PAGE_LIMIT });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to load contacts."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, page]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === "applications") loadApps();
    else if (tab === "contacts") loadContacts();
  }, [tab, loadApps, loadContacts]);

  // Reset filters + pagination when switching tabs
  useEffect(() => {
    setStatusFilter("all");
    setCityFilter("all");
    setSearch("");
    setPage(1);
  }, [tab]);

  // Reset to page 1 whenever filters/search change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, cityFilter, search]);

  const updateAppStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/applications/${id}`, { status: newStatus });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      if (selectedApp?.id === id) setSelectedApp(data);
      toast.success(`Status updated to "${newStatus}".`);
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to update status."));
    }
  };

  const updateContactStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/contacts/${id}`, { status: newStatus });
      setContacts((prev) => prev.map((c) => (c.id === id ? data : c)));
      if (selectedContact?.id === id) setSelectedContact(data);
      toast.success(`Status updated to "${newStatus}".`);
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to update status."));
    }
  };

  const addAppNote = async (id, text) => {
    const { data } = await api.post(`/admin/applications/${id}/notes`, { text });
    setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
    if (selectedApp?.id === id) setSelectedApp(data);
    return data;
  };

  const deleteAppNote = async (id, noteId) => {
    const { data } = await api.delete(`/admin/applications/${id}/notes/${noteId}`);
    setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
    if (selectedApp?.id === id) setSelectedApp(data);
    return data;
  };

  const exportApplicationsCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("vf_admin_token");
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (cityFilter !== "all") params.set("city", cityFilter);
      if (search.trim()) params.set("q", search.trim());
      const url = `${API_BASE}/admin/applications/export.csv${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const disp = res.headers.get("content-disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : "applications.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("CSV exported.");
    } catch (err) {
      toast.error("Failed to export CSV.");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const cities = Array.from(new Set(apps.map((a) => a.city).filter(Boolean))).sort();
  const statusOptions = tab === "applications" ? APP_STATUSES : CONTACT_STATUSES;

  const openCreatePOForApp = (a) => {
    setPoPrefill({
      application_id: a.id,
      lessee_name: a.full_name,
      lessee_email: a.email,
      lessee_phone: a.phone,
      vehicle_make: (a.vehicle_interest || "").split(" ")[0] || "",
      vehicle_model: (a.vehicle_interest || "").split(" ").slice(1).join(" ") || "",
      vehicle_price: "",
    });
    setTab("pos");
    setSelectedApp(null);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Topbar */}
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <span className="hidden md:inline text-xs uppercase tracking-[0.22em] text-white/40 border-l border-white/10 pl-8">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-xs text-white/40">Signed in</span>
              <span className="text-sm text-white/80">{user?.email}</span>
            </div>
            <Link
              to="/admin/documents"
              data-testid="admin-documents-link"
              className="hidden sm:inline text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
            >
              Documents →
            </Link>
            <Link
              to="/admin/fleet"
              data-testid="admin-fleet-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Fleet →
            </Link>
            <Link
              to="/admin/tolls"
              data-testid="admin-tolls-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Tolls →
            </Link>
            <Link
              to="/admin/maintenance"
              data-testid="admin-maintenance-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Maintenance →
            </Link>
            <Link
              to="/admin/fleet-map"
              data-testid="admin-fleet-map-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Map →
            </Link>
            <Link
              to="/admin/identity"
              data-testid="admin-identity-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Identity →
            </Link>
            <Link
              to="/admin/digest"
              data-testid="admin-digest-link"
              className="hidden sm:inline text-xs text-[#D4AF37] font-medium hover:text-[#F6ECCC] tracking-wide"
            >
              Digest →
            </Link>
            <Link
              to="/admin/signature"
              data-testid="admin-signature-link"
              className="hidden sm:inline text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
            >
              Email signature →
            </Link>
            <Link
              to="/admin/password"
              data-testid="admin-password-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
            >
              <KeyRound size={12} /> Change password
            </Link>
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden sm:inline text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
            >
              View site →
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-4 py-2 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        {/* Heading */}
        <div className="mb-10">
          <div className="label-eyebrow mb-3">Overview</div>
          <h1
            className="font-display text-4xl md:text-5xl text-white"
            style={{ fontWeight: 300 }}
          >
            Welcome back, <span className="italic text-[#D4AF37]">{user?.name?.split(" ")[0] || "Admin"}</span>.
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={<FileText size={16} />}
            label="Total Applications"
            value={stats?.applications?.total ?? "—"}
            testId="stat-apps-total"
          />
          <StatCard
            icon={<Clock size={16} />}
            label="New (Last 7 Days)"
            value={stats?.applications?.last_7_days ?? "—"}
            testId="stat-apps-recent"
            accent
          />
          <StatCard
            icon={<ShoppingCart size={16} />}
            label="Purchase Orders"
            value={stats?.purchase_orders?.total ?? "—"}
            testId="stat-pos-total"
          />
          <StatCard
            icon={<MessageSquare size={16} />}
            label="New Enquiries"
            value={stats?.contacts?.new ?? "—"}
            testId="stat-contacts-new"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/10 mb-6 overflow-x-auto">
          <TabButton
            active={tab === "applications"}
            onClick={() => setTab("applications")}
            icon={<Users size={14} />}
            testId="tab-applications"
          >
            Applications {stats?.applications?.total != null && <span className="text-white/40 ml-1">({stats.applications.total})</span>}
          </TabButton>
          <TabButton
            active={tab === "pos"}
            onClick={() => setTab("pos")}
            icon={<ShoppingCart size={14} />}
            testId="tab-pos"
          >
            Purchase Orders {stats?.purchase_orders?.total != null && <span className="text-white/40 ml-1">({stats.purchase_orders.total})</span>}
          </TabButton>
          <TabButton
            active={tab === "contacts"}
            onClick={() => setTab("contacts")}
            icon={<MessageSquare size={14} />}
            testId="tab-contacts"
          >
            Contacts {stats?.contacts?.total != null && <span className="text-white/40 ml-1">({stats.contacts.total})</span>}
          </TabButton>
        </div>

        {/* PO tab — full section with its own toolbar */}
        {tab === "pos" && (
          <PurchaseOrders
            prefill={poPrefill}
            onClearPrefill={() => setPoPrefill(null)}
          />
        )}

        {/* Filters — only for apps/contacts */}
        {tab !== "pos" && (
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              data-testid="admin-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                tab === "applications"
                  ? "Search name, email, phone, ABN, vehicle…"
                  : "Search name, email, subject, message…"
              }
              className="w-full bg-[#0a0a0a] border border-white/10 text-white pl-10 pr-4 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
            />
          </div>

          <select
            data-testid="admin-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#0a0a0a] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none capitalize"
          >
            <option value="all">All statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s}
              </option>
            ))}
          </select>

          {tab === "applications" && (
            <select
              data-testid="admin-city-filter"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="all">All cities</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}

          {tab === "applications" && (
            <button
              onClick={exportApplicationsCsv}
              disabled={exporting}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2.5 text-sm hover:bg-[#D4AF37]/10 disabled:opacity-50 transition-colors"
            >
              <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )}
        </div>
        )}

        {/* List */}
        {tab !== "pos" && (
        <div className="bg-[#0a0a0a] border border-white/10">
          {loading ? (
            <div className="p-16 text-center text-white/50 text-sm">Loading…</div>
          ) : tab === "applications" ? (
            <ApplicationsTable
              apps={apps}
              onSelect={setSelectedApp}
              onStatus={updateAppStatus}
            />
          ) : (
            <ContactsTable
              contacts={contacts}
              onSelect={setSelectedContact}
              onStatus={updateContactStatus}
            />
          )}
        </div>
        )}

        {/* Pagination */}
        {tab !== "pos" && !loading && pageInfo.total > 0 && (
          <Pagination
            page={page}
            pages={pageInfo.pages}
            total={pageInfo.total}
            limit={pageInfo.limit}
            onChange={setPage}
          />
        )}
      </main>

      {/* App detail drawer */}
      {selectedApp && (
        <ApplicationDrawer
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onStatus={updateAppStatus}
          onCreatePO={openCreatePOForApp}
          onAddNote={addAppNote}
          onDeleteNote={deleteAppNote}
        />
      )}

      {/* Contact detail drawer */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onStatus={updateContactStatus}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className={`bg-[#0a0a0a] border ${accent ? "border-[#D4AF37]/30" : "border-white/10"} p-5`}
    >
      <div className={`flex items-center gap-2 mb-3 ${accent ? "text-[#D4AF37]" : "text-white/50"}`}>
        {icon}
        <span className="text-[10px] uppercase tracking-[0.2em]">{label}</span>
      </div>
      <div className="font-display text-3xl text-white" style={{ fontWeight: 300 }}>
        {value}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors ${
        active
          ? "border-[#D4AF37] text-white"
          : "border-transparent text-white/50 hover:text-white"
      }`}
    >
      {icon} {children}
    </button>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
        STATUS_COLORS[status] || STATUS_COLORS.archived
      }`}
    >
      {status}
    </span>
  );
}

function ApplicationsTable({ apps, onSelect, onStatus }) {
  if (apps.length === 0) {
    return (
      <div className="p-16 text-center text-white/40 text-sm" data-testid="apps-empty">
        No applications match your filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#080808] border-b border-white/10">
          <tr className="text-left text-white/50 text-[10px] uppercase tracking-[0.18em]">
            <th className="px-5 py-4">Applicant</th>
            <th className="px-5 py-4 hidden md:table-cell">City</th>
            <th className="px-5 py-4 hidden lg:table-cell">Vehicle</th>
            <th className="px-5 py-4 hidden md:table-cell">Term</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4 hidden lg:table-cell">Submitted</th>
            <th className="px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {apps.map((a) => (
            <tr
              key={a.id}
              data-testid={`app-row-${a.id}`}
              className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <td className="px-5 py-4">
                <div className="text-white">{a.full_name}</div>
                <div className="text-xs text-white/40">{a.email}</div>
              </td>
              <td className="px-5 py-4 hidden md:table-cell text-white/70">{a.city}</td>
              <td className="px-5 py-4 hidden lg:table-cell text-white/70">{a.vehicle_interest}</td>
              <td className="px-5 py-4 hidden md:table-cell text-white/70">{a.term_years}y</td>
              <td className="px-5 py-4"><StatusBadge status={a.status || "new"} /></td>
              <td className="px-5 py-4 hidden lg:table-cell text-white/50 text-xs">{fmtDate(a.created_at)}</td>
              <td className="px-5 py-4 text-right">
                <div className="flex items-center gap-2 justify-end">
                  <select
                    data-testid={`app-status-select-${a.id}`}
                    value={a.status || "new"}
                    onChange={(e) => onStatus(a.id, e.target.value)}
                    className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#D4AF37] focus:outline-none capitalize"
                  >
                    {APP_STATUSES.map((s) => (
                      <option key={s} value={s} className="capitalize">{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => onSelect(a)}
                    data-testid={`app-view-${a.id}`}
                    className="text-xs text-[#D4AF37] hover:text-[#F6ECCC]"
                  >
                    View
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsTable({ contacts, onSelect, onStatus }) {
  if (contacts.length === 0) {
    return (
      <div className="p-16 text-center text-white/40 text-sm" data-testid="contacts-empty">
        No enquiries match your filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#080808] border-b border-white/10">
          <tr className="text-left text-white/50 text-[10px] uppercase tracking-[0.18em]">
            <th className="px-5 py-4">From</th>
            <th className="px-5 py-4 hidden md:table-cell">Subject</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4 hidden lg:table-cell">Received</th>
            <th className="px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr
              key={c.id}
              data-testid={`contact-row-${c.id}`}
              className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <td className="px-5 py-4">
                <div className="text-white">{c.name}</div>
                <div className="text-xs text-white/40">{c.email}</div>
              </td>
              <td className="px-5 py-4 hidden md:table-cell text-white/70 max-w-xs truncate">{c.subject}</td>
              <td className="px-5 py-4"><StatusBadge status={c.status || "new"} /></td>
              <td className="px-5 py-4 hidden lg:table-cell text-white/50 text-xs">{fmtDate(c.created_at)}</td>
              <td className="px-5 py-4 text-right">
                <div className="flex items-center gap-2 justify-end">
                  <select
                    data-testid={`contact-status-select-${c.id}`}
                    value={c.status || "new"}
                    onChange={(e) => onStatus(c.id, e.target.value)}
                    className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#D4AF37] focus:outline-none capitalize"
                  >
                    {CONTACT_STATUSES.map((s) => (
                      <option key={s} value={s} className="capitalize">{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => onSelect(c)}
                    data-testid={`contact-view-${c.id}`}
                    className="text-xs text-[#D4AF37] hover:text-[#F6ECCC]"
                  >
                    View
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DrawerShell({ title, subtitle, onClose, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl h-full bg-[#0a0a0a] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow mb-1">{subtitle}</div>
            <h2 className="font-display text-2xl text-white" style={{ fontWeight: 400 }}>{title}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="text-white/60 hover:text-white p-1"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-6 space-y-5">{children}</div>
      </div>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-0.5">{label}</div>
        <div className="text-sm text-white break-words">{value || "—"}</div>
      </div>
    </div>
  );
}

function ApplicationDrawer({ app, onClose, onStatus, onCreatePO, onAddNote, onDeleteNote }) {
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const notes = app.notes || [];

  const submitNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      await onAddNote(app.id, text);
      setNoteText("");
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to add note."));
    } finally {
      setSavingNote(false);
    }
  };

  const removeNote = async (noteId) => {
    try {
      await onDeleteNote(app.id, noteId);
      toast.success("Note removed.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to remove note."));
    }
  };

  return (
    <DrawerShell
      title={app.full_name}
      subtitle={`Application · ${app.id.slice(0, 8)}`}
      onClose={onClose}
      testId="app-drawer"
    >
      <div className="flex items-center justify-between border border-white/10 bg-[#050505] p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-white/40">Status</div>
        <select
          data-testid="drawer-app-status"
          value={app.status || "new"}
          onChange={(e) => onStatus(app.id, e.target.value)}
          className="bg-[#0a0a0a] border border-white/15 text-white text-sm px-3 py-1.5 focus:border-[#D4AF37] focus:outline-none capitalize"
        >
          {APP_STATUSES.map((s) => (
            <option key={s} value={s} className="capitalize">{s}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Detail icon={<Mail size={14} />} label="Email" value={app.email} />
        <Detail icon={<Phone size={14} />} label="Phone" value={app.phone} />
        <Detail icon={<MapPin size={14} />} label="City" value={app.city} />
        <Detail icon={<Calendar size={14} />} label="Date of Birth" value={app.date_of_birth} />
        <Detail icon={<Building2 size={14} />} label="ABN" value={app.abn} />
        <Detail icon={<CreditCard size={14} />} label="Driver's Licence" value={app.drivers_license} />
        <Detail icon={<Briefcase size={14} />} label="Employment" value={app.employment_status} />
        <Detail icon={<DollarSign size={14} />} label="Monthly Income" value={app.monthly_income} />
        <Detail icon={<Car size={14} />} label="Vehicle Interest" value={app.vehicle_interest} />
        <Detail icon={<DollarSign size={14} />} label="Deposit" value={`$${app.deposit_amount}`} />
        <Detail icon={<Clock size={14} />} label="Term" value={`${app.term_years} years`} />
        {app.additional_notes && (
          <div className="border border-white/10 bg-[#050505] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Applicant Notes</div>
            <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{app.additional_notes}</div>
          </div>
        )}
        <Detail icon={<Calendar size={14} />} label="Submitted" value={fmtDate(app.created_at)} />
      </div>

      {/* Admin Notes */}
      <div className="border border-[#D4AF37]/25 bg-[#050505] p-4 mt-2">
        <div className="flex items-center gap-2 mb-4">
          <StickyNote size={14} className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Admin Notes</span>
          <span className="text-[10px] text-white/40">({notes.length})</span>
        </div>

        {notes.length === 0 ? (
          <div className="text-xs text-white/40 mb-4" data-testid="app-notes-empty">
            No notes yet. Add the first one below.
          </div>
        ) : (
          <ul className="space-y-3 mb-4" data-testid="app-notes-list">
            {notes.map((n) => (
              <li
                key={n.id}
                data-testid={`app-note-${n.id}`}
                className="border-l-2 border-[#D4AF37]/50 pl-3 py-1 flex items-start justify-between gap-3"
              >
                <div className="flex-1">
                  <div className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">{n.text}</div>
                  <div className="text-[10px] text-white/40 mt-1">
                    {n.author} · {fmtDate(n.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => removeNote(n.id)}
                  data-testid={`app-note-delete-${n.id}`}
                  className="text-white/40 hover:text-red-400 p-1 flex-shrink-0"
                  title="Delete note"
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-start gap-2">
          <textarea
            data-testid="app-note-input"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a follow-up note…"
            rows={2}
            className="flex-1 bg-[#0a0a0a] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none"
          />
          <button
            onClick={submitNote}
            disabled={savingNote || !noteText.trim()}
            data-testid="app-note-submit"
            className="bg-[#D4AF37] text-black text-xs font-medium px-3 py-2 hover:bg-[#F6ECCC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
          >
            <Send size={12} /> {savingNote ? "Saving…" : "Add"}
          </button>
        </div>
      </div>

      <a
        href={`mailto:${app.email}?subject=Keys to Own — Your Application`}
        data-testid="drawer-app-reply"
        className="block text-center bg-[#D4AF37] text-black font-medium py-3 mt-6 hover:bg-[#F6ECCC] transition-colors"
      >
        Reply via Email
      </a>
      <button
        onClick={() => onCreatePO && onCreatePO(app)}
        data-testid="drawer-app-create-po"
        className="w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors mt-3"
      >
        <FilePlus size={14} /> Create Purchase Order
      </button>
    </DrawerShell>
  );
}

function Pagination({ page, pages, total, limit, onChange }) {
  const startIdx = (page - 1) * limit + 1;
  const endIdx = Math.min(page * limit, total);
  return (
    <div
      data-testid="admin-pagination"
      className="flex items-center justify-between border border-t-0 border-white/10 bg-[#0a0a0a] px-5 py-3 text-xs"
    >
      <div className="text-white/50" data-testid="admin-pagination-summary">
        Showing <span className="text-white">{startIdx}</span>–<span className="text-white">{endIdx}</span> of{" "}
        <span className="text-white">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="admin-pagination-prev"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 border border-white/10 text-white/70 px-3 py-1.5 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={13} /> Prev
        </button>
        <span className="text-white/60 px-2" data-testid="admin-pagination-page">
          Page {page} of {pages}
        </span>
        <button
          data-testid="admin-pagination-next"
          onClick={() => onChange(Math.min(pages, page + 1))}
          disabled={page >= pages}
          className="inline-flex items-center gap-1 border border-white/10 text-white/70 px-3 py-1.5 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Next <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

function ContactDrawer({ contact, onClose, onStatus }) {
  return (
    <DrawerShell
      title={contact.name}
      subtitle={`Contact · ${contact.id.slice(0, 8)}`}
      onClose={onClose}
      testId="contact-drawer"
    >
      <div className="flex items-center justify-between border border-white/10 bg-[#050505] p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-white/40">Status</div>
        <select
          data-testid="drawer-contact-status"
          value={contact.status || "new"}
          onChange={(e) => onStatus(contact.id, e.target.value)}
          className="bg-[#0a0a0a] border border-white/15 text-white text-sm px-3 py-1.5 focus:border-[#D4AF37] focus:outline-none capitalize"
        >
          {CONTACT_STATUSES.map((s) => (
            <option key={s} value={s} className="capitalize">{s}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Detail icon={<Mail size={14} />} label="Email" value={contact.email} />
        <Detail icon={<Phone size={14} />} label="Phone" value={contact.phone || "Not provided"} />
        <Detail icon={<MessageSquare size={14} />} label="Subject" value={contact.subject} />
        <div className="border border-white/10 bg-[#050505] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Message</div>
          <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{contact.message}</div>
        </div>
        <Detail icon={<Calendar size={14} />} label="Received" value={fmtDate(contact.created_at)} />
      </div>

      <a
        href={`mailto:${contact.email}?subject=Re: ${encodeURIComponent(contact.subject)}`}
        data-testid="drawer-contact-reply"
        className="block text-center bg-[#D4AF37] text-black font-medium py-3 mt-6 hover:bg-[#F6ECCC] transition-colors"
      >
        Reply via Email
      </a>
    </DrawerShell>
  );
}
